import React from 'react'
import { cm } from '~/utils/utils'

// Icon components
const UndoIcon = () => (
  <svg width='16' height='16' viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M4.872 6.408L5.77333 5.79267C5.84502 5.74377 5.92565 5.70963 6.01059 5.69219C6.09553 5.67474 6.18316 5.67435 6.26835 5.69111C6.35354 5.70786 6.43435 5.74142 6.50645 5.78972C6.57855 5.83803 6.64041 5.90019 6.688 5.97267C6.78569 6.11944 6.82177 6.29853 6.78867 6.47163C6.75556 6.64472 6.65568 6.79784 6.51067 6.898L4.326 8.38933C4.25431 8.43825 4.17369 8.47239 4.08875 8.48986C4.00381 8.50732 3.91615 8.50773 3.83096 8.49092C3.74577 8.47412 3.66491 8.44057 3.59287 8.39227C3.52083 8.34396 3.45907 8.28184 3.41133 8.20933L1.938 5.99867C1.83994 5.85203 1.80357 5.67262 1.83675 5.49933C1.86994 5.32604 1.97001 5.17284 2.11533 5.07267C2.18698 5.02377 2.26765 4.98963 2.35264 4.97213C2.43763 4.95467 2.52523 4.95435 2.61036 4.97111C2.69549 4.98786 2.77643 5.02142 2.84847 5.06972C2.92052 5.11803 2.98222 5.18019 3.03 5.25267L3.58933 6.092C4.4 3.34 6.92 1.33334 9.90333 1.33334C13.542 1.33334 16.4913 4.318 16.4913 8C16.4913 11.682 13.542 14.6667 9.90333 14.6667C9.81631 14.6662 9.73019 14.6485 9.64999 14.6147C9.56979 14.5809 9.49705 14.5317 9.43593 14.4698C9.37481 14.4079 9.32636 14.3345 9.29356 14.2539C9.26076 14.1733 9.24413 14.087 9.24467 14C9.24467 13.632 9.54 13.3333 9.90333 13.3333C12.814 13.3333 15.174 10.9453 15.174 8C15.174 5.05467 12.814 2.66667 9.90333 2.66667C7.54067 2.66667 5.54133 4.24 4.872 6.408Z'
      fill='rgba(0, 0, 0, 0.45)'
    />
  </svg>
)

const CheckIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M8.00001 14.4C11.5346 14.4 14.4 11.5346 14.4 8.00001C14.4 4.46538 11.5346 1.60001 8.00001 1.60001C4.46538 1.60001 1.60001 4.46538 1.60001 8.00001C1.60001 11.5346 4.46538 14.4 8.00001 14.4ZM10.9657 6.96569C11.2781 6.65327 11.2781 6.14674 10.9657 5.83432C10.6533 5.5219 10.1467 5.5219 9.83432 5.83432L7.20001 8.46863L6.16569 7.43432C5.85327 7.1219 5.34674 7.1219 5.03432 7.43432C4.7219 7.74674 4.7219 8.25327 5.03432 8.56569L6.63432 10.1657C6.94674 10.4781 7.45327 10.4781 7.76569 10.1657L10.9657 6.96569Z'
      fill='#2C8450'
    />
  </svg>
)

export type PluginTaskCardStatus = 'ready' | 'applied' | 'completed' | 'in-progress'

export interface PluginTaskCardProps extends React.HTMLAttributes<HTMLDivElement> {
  taskId?: string
  taskTitle?: string
  status?: PluginTaskCardStatus
  statusText?: string
  onViewTask?: () => void
  onApplyChanges?: () => void
  onUndo?: () => void
}

const statusConfig = {
  ready: {
    tagText: 'Ready to Apply',
    tagTextColor: 'text-[#846fe1]',
    tagBgColor: 'bg-[rgba(0,0,0,0.06)]'
  },
  applied: {
    tagText: 'Applied',
    tagTextColor: 'text-[rgba(0,0,0,0.45)]',
    tagBgColor: 'bg-[rgba(0,0,0,0.06)]'
  },
  completed: {
    tagText: 'Task Completed',
    tagTextColor: 'text-[rgba(0,0,0,0.45)]',
    tagBgColor: 'bg-[rgba(0,0,0,0.06)]'
  },
  'in-progress': {
    tagText: 'Task In Progress',
    tagTextColor: 'text-[rgba(0,0,0,0.45)]',
    tagBgColor: 'bg-[rgba(0,0,0,0.06)]'
  }
}

export const PluginTaskCard = React.forwardRef<HTMLDivElement, PluginTaskCardProps>(
  (
    {
      taskId = '45632',
      taskTitle = 'Add a flow',
      status = 'ready',
      statusText,
      onViewTask,
      onApplyChanges,
      onUndo,
      className,
      ...props
    },
    ref
  ) => {
    const config = statusConfig[status]
    const displayStatusText = statusText || config.tagText

    return (
      <div
        ref={ref}
        className={cm(
          'bg-[rgba(0,0,0,0.03)] relative rounded-[16px] w-[354px] h-[125px]',
          className
        )}
        {...props}
      >
        <div className='flex flex-col items-center relative size-full'>
          <div className='box-border content-stretch flex flex-col gap-4 items-center justify-start overflow-clip px-0 py-4 relative size-full'>
            {/* Header Section */}
            <div className='relative shrink-0 w-full'>
              <div className='flex flex-col items-center relative size-full'>
                <div className='box-border content-stretch flex flex-col gap-2 items-center justify-start px-6 py-0 relative w-full'>
                  <div className='box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full'>
                    <div className='box-border content-stretch flex flex-col gap-2 items-start justify-center p-0 relative shrink-0'>
                      {/* Task Title */}
                      <div className='box-border content-stretch flex flex-row gap-1 items-center justify-start p-0 relative shrink-0'>
                        <div className="font-['Geist'] font-semibold leading-[0] relative shrink-0 text-[14px] text-[rgba(0,0,0,0.88)] text-left text-nowrap">
                          <p className='block leading-[18px] whitespace-pre'>
                            Task{taskId} - {taskTitle}
                          </p>
                        </div>
                      </div>
                      {/* Status Tag */}
                      <div
                        className={cm('h-5 relative rounded-[50px] shrink-0', config.tagBgColor)}
                      >
                        <div className='flex flex-row items-center relative size-full'>
                          <div className='box-border content-stretch flex flex-row gap-1 h-5 items-center justify-start p-[8px] relative'>
                            <div
                              className={cm(
                                "font-['Geist_Mono'] font-bold leading-[0] relative shrink-0 text-[10px] text-left text-nowrap",
                                config.tagTextColor
                              )}
                            >
                              <p className='block leading-[normal] whitespace-pre'>
                                {displayStatusText}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Section */}
            <div className='relative shrink-0 w-full'>
              <div className='flex flex-row items-center justify-end relative size-full'>
                <div className='box-border content-stretch flex flex-row gap-1 items-center justify-end px-6 py-0 relative w-full'>
                  {status === 'ready' && (
                    <>
                      {/* View Task Button */}
                      <button
                        onClick={onViewTask}
                        className='box-border content-stretch flex flex-col gap-1.5 items-center justify-center overflow-clip p-0 relative rounded-xl shrink-0 hover:bg-[rgba(0,0,0,0.03)] transition-colors'
                      >
                        <div className='relative shrink-0 w-full'>
                          <div className='flex flex-row items-center justify-center relative size-full'>
                            <div className='box-border content-stretch flex flex-row gap-2 items-center justify-center p-[6px] relative w-full'>
                              <div className="flex flex-col font-['Geist'] font-bold justify-center leading-[0] relative shrink-0 text-[#65558f] text-[12px] text-center text-nowrap">
                                <p className='block leading-[normal] whitespace-pre'>View Task</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                      {/* Apply Changes Button */}
                      <button
                        onClick={onApplyChanges}
                        className='bg-[#2e1f36] box-border content-stretch flex flex-col gap-2 items-center justify-center overflow-clip p-0 relative rounded-xl shrink-0 hover:bg-[#3a2844] transition-colors'
                      >
                        <div className='relative shrink-0 w-full'>
                          <div className='flex flex-row items-center justify-center relative size-full'>
                            <div className='box-border content-stretch flex flex-row gap-2 items-center justify-center p-[8px] relative w-full'>
                              <div className="flex flex-col font-['Geist'] font-semibold justify-center leading-[0] relative shrink-0 text-[#ffffff] text-[12px] text-center text-nowrap">
                                <p className='block leading-[normal] whitespace-pre'>
                                  Apply Changes
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    </>
                  )}

                  {(status === 'applied' || status === 'completed') && (
                    <>
                      {/* Undo Button - only show if onUndo handler is provided */}
                      {onUndo && (
                        <button
                          onClick={onUndo}
                          className='box-border content-stretch flex flex-col gap-2 items-center justify-center overflow-clip p-0 relative rounded-xl shrink-0 hover:bg-[rgba(0,0,0,0.03)] transition-colors'
                        >
                          <div className='relative shrink-0 w-full'>
                            <div className='flex flex-row items-center justify-center relative size-full'>
                              <div className='box-border content-stretch flex flex-row gap-2 items-center justify-center p-[8px] relative w-full'>
                                <div className='overflow-clip relative shrink-0 size-4'>
                                  <UndoIcon />
                                </div>
                                <div className="flex flex-col font-['Geist'] font-bold justify-center leading-[0] relative shrink-0 text-[#65558f] text-[12px] text-center text-nowrap">
                                  <p className='block leading-[normal] whitespace-pre'>Undo</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </button>
                      )}
                      {/* Applied Button (disabled) - only show if onUndo handler is provided */}
                      {onUndo && (
                        <div className='box-border content-stretch flex flex-col gap-2 items-center justify-center overflow-clip p-0 relative rounded-xl shrink-0 opacity-60'>
                          <div className='bg-[rgba(0,0,0,0.06)] relative shrink-0 w-full'>
                            <div className='flex flex-row items-center justify-center relative size-full'>
                              <div className='box-border content-stretch flex flex-row gap-2 items-center justify-center p-[8px] relative w-full'>
                                <div className='overflow-clip relative shrink-0 size-4'>
                                  <CheckIcon />
                                </div>
                                <div className="flex flex-col font-['Geist'] font-semibold justify-center leading-[0] relative shrink-0 text-[12px] text-[rgba(0,0,0,0.45)] text-center text-nowrap">
                                  <p className='block leading-[normal] whitespace-pre'>Applied</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* View Task Button - show for errored tasks */}
                      {!onUndo && (
                        <button
                          onClick={onViewTask}
                          className='box-border content-stretch flex flex-col gap-1.5 items-center justify-center overflow-clip p-0 relative rounded-xl shrink-0 hover:bg-[rgba(0,0,0,0.03)] transition-colors'
                        >
                          <div className='relative shrink-0 w-full'>
                            <div className='flex flex-row items-center justify-center relative size-full'>
                              <div className='box-border content-stretch flex flex-row gap-2 items-center justify-center p-[8px] relative w-full'>
                                <div className="flex flex-col font-['Geist'] font-bold justify-center leading-[0] relative shrink-0 text-[#65558f] text-[12px] text-center text-nowrap">
                                  <p className='block leading-[normal] whitespace-pre'>View Task</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </button>
                      )}
                    </>
                  )}

                  {status === 'in-progress' && (
                    /* View Task Button */
                    <button
                      onClick={onViewTask}
                      className='box-border content-stretch flex flex-col gap-1.5 items-center justify-center overflow-clip p-0 relative rounded-xl shrink-0 hover:bg-[rgba(0,0,0,0.03)] transition-colors'
                    >
                      <div className='relative shrink-0 w-full'>
                        <div className='flex flex-row items-center justify-center relative size-full'>
                          <div className='box-border content-stretch flex flex-row gap-2 items-center justify-center p-[6px] relative w-full'>
                            <div className="flex flex-col font-['Geist'] font-bold justify-center leading-[0] relative shrink-0 text-[#65558f] text-[12px] text-center text-nowrap">
                              <p className='block leading-[normal] whitespace-pre'>View Task</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Border */}
        <div className='absolute border border-[#eaeaea] border-solid inset-0 pointer-events-none rounded-2xl' />
      </div>
    )
  }
)

PluginTaskCard.displayName = 'PluginTaskCard'

export default PluginTaskCard
